import { ByLanguage, ImpactLanguage } from '../types';
import {
  EuActivityType,
  EuActivityTypes,
  EuNfrdAssessmentType,
  EuObjective,
  EuObjectives,
  EuTaxonomyAssessmentType,
  TnfaAssessmentType,
} from './types';

export function friendlyShortObjectiveTitle(objective: EuObjectives) {
  switch (objective) {
    case EuObjectives.mitigationNuclear:
      return 'Nuclear energy';
    case EuObjectives.mitigationFossilGas:
      return 'Fossil gaseous fuels';
    case EuObjectives.mitigationOther:
      // Not very descriptive, but also not in use
      return 'Climate mitigation excl. nuclear energy and fossil gaseous fuels';
    case EuObjectives.mitigation:
      return 'Climate mitigation';
    case EuObjectives.adaptation:
      return 'Climate adaptation';
    case EuObjectives.water:
      return 'Water resources';
    case EuObjectives.circular:
      return 'Circular economy';
    case EuObjectives.pollution:
      return 'Pollution';
    case EuObjectives.biodiversity:
      return 'Biodiversity';
    case EuObjectives.climate:
      return 'Mitigation or adaptation';
    case EuObjectives.union:
      return 'Total';
  }
}

export function friendlyObjectiveTitle(objective: EuObjective, ellaborateUnion = false) {
  if (ellaborateUnion && objective === EuObjectives.union) {
    return 'Any EU taxonomy objective';
  }
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return objectiveNamesByLanguage.en![objective];
}

const objectiveNamesByLanguage: Partial<ByLanguage<Record<EuObjectives, string>>> = {
  en: {
    [EuObjectives.mitigationNuclear]: 'Nuclear energy',
    [EuObjectives.mitigationFossilGas]: 'Fossil gaseous fuels',
    [EuObjectives.mitigationOther]: 'Climate change mitigation excl. nuclear energy or fossil gaseous fuels',
    [EuObjectives.mitigation]: 'Climate change mitigation',
    [EuObjectives.adaptation]: 'Climate change adaptation',
    [EuObjectives.water]: 'Sustainable use of water and marine resources',
    [EuObjectives.circular]: 'Transition to circular economy',
    [EuObjectives.pollution]: 'Pollution prevention and control',
    [EuObjectives.biodiversity]: 'Protection and restoration of biodiversity and ecosystems',
    [EuObjectives.climate]: 'Climate change mitigation or adaptation',
    [EuObjectives.union]: 'Total',
  },
  // TODO translate and use in api/metrics/legend
  // fi: {},
  // sv: {},
};

export function friendlyEuActivityType(activityType: EuActivityType) {
  return activityTypeNamesByLanguage.en[activityType];
}

type ByEnglishLanguage<T> = Pick<ByLanguage<T>, ImpactLanguage.en>;
export const activityTypeNamesByLanguage: ByEnglishLanguage<Record<EuActivityTypes, string>> = {
  en: {
    [EuActivityTypes.transitional]: 'Transitional',
    [EuActivityTypes.enabling]: 'Enabling',
    [EuActivityTypes.direct]: 'Direct',
    [EuActivityTypes.any]: 'Any',
  },
};

export function friendlyEuAssessmentTitle(
  metric: EuTaxonomyAssessmentType | EuNfrdAssessmentType | TnfaAssessmentType,
) {
  switch (metric) {
    case EuNfrdAssessmentType.NFRD_OBLIGATION:
      return 'NFRD obligation';

    case EuTaxonomyAssessmentType.MITIGATION_REVENUE_ELIGIBILITY:
      return 'Mitigation eligibility (revenue based)';
    case EuTaxonomyAssessmentType.MITIGATION_REVENUE_ALIGNMENT:
      return 'Mitigation alignment (revenue based)';
    case EuTaxonomyAssessmentType.ADAPTATION_REVENUE_ELIGIBILITY:
      return 'Adaptation eligibility (revenue based)';
    case EuTaxonomyAssessmentType.ADAPTATION_REVENUE_ALIGNMENT:
      return 'Adaptation alignment (revenue based)';
    case EuTaxonomyAssessmentType.WATER_REVENUE_ELIGIBILITY:
      return 'Water eligibility (revenue based)';
    case EuTaxonomyAssessmentType.WATER_REVENUE_ALIGNMENT:
      return 'Water alignment (revenue based)';
    case EuTaxonomyAssessmentType.CIRCULAR_REVENUE_ELIGIBILITY:
      return 'Circular eligibility (revenue based)';
    case EuTaxonomyAssessmentType.CIRCULAR_REVENUE_ALIGNMENT:
      return 'Circular alignment (revenue based)';
    case EuTaxonomyAssessmentType.POLLUTION_REVENUE_ELIGIBILITY:
      return 'Pollution eligibility (revenue based)';
    case EuTaxonomyAssessmentType.POLLUTION_REVENUE_ALIGNMENT:
      return 'Pollution alignment (revenue based)';
    case EuTaxonomyAssessmentType.BIODIVERSITY_REVENUE_ELIGIBILITY:
      return 'Biodiversity eligibility (revenue based)';
    case EuTaxonomyAssessmentType.BIODIVERSITY_REVENUE_ALIGNMENT:
      return 'Biodiversity alignment (revenue based)';
    case EuTaxonomyAssessmentType.MITIGATION_OR_ADAPTATION_REVENUE_ELIGIBILITY:
      return 'Mitig, & Adapt. eligibility (revenue based)';
    case EuTaxonomyAssessmentType.MITIGATION_OR_ADAPTATION_REVENUE_ALIGNMENT:
      return 'Mitig, & Adapt. alignment (revenue based)';
    case EuTaxonomyAssessmentType.UNION_REVENUE_ELIGIBILITY:
      return 'Total eligibility (revenue based)';
    case EuTaxonomyAssessmentType.UNION_REVENUE_ALIGNMENT:
      return 'Total alignment (revenue based)';
    case EuTaxonomyAssessmentType.UNION_REVENUE_ALIGNMENT_ENABLING:
      return 'Total alignment (revenue based, enabling)';
    case EuTaxonomyAssessmentType.UNION_REVENUE_ALIGNMENT_TRANSITIONAL:
      return 'Total alignment (revenue based, transitional)';

    case EuTaxonomyAssessmentType.MITIGATION_CAPEX_ELIGIBILITY:
      return 'Mitigation eligibility (CAPEX based)';
    case EuTaxonomyAssessmentType.MITIGATION_CAPEX_ALIGNMENT:
      return 'Mitigation alignment (CAPEX based)';
    case EuTaxonomyAssessmentType.ADAPTATION_CAPEX_ELIGIBILITY:
      return 'Adaptation eligibility (CAPEX based)';
    case EuTaxonomyAssessmentType.ADAPTATION_CAPEX_ALIGNMENT:
      return 'Adaptation alignment (CAPEX based)';
    case EuTaxonomyAssessmentType.WATER_CAPEX_ELIGIBILITY:
      return 'Water eligibility (CAPEX based)';
    case EuTaxonomyAssessmentType.WATER_CAPEX_ALIGNMENT:
      return 'Water alignment (CAPEX based)';
    case EuTaxonomyAssessmentType.CIRCULAR_CAPEX_ELIGIBILITY:
      return 'Circular eligibility (CAPEX based)';
    case EuTaxonomyAssessmentType.CIRCULAR_CAPEX_ALIGNMENT:
      return 'Circular alignment (CAPEX based)';
    case EuTaxonomyAssessmentType.POLLUTION_CAPEX_ELIGIBILITY:
      return 'Pollution eligibility (CAPEX based)';
    case EuTaxonomyAssessmentType.POLLUTION_CAPEX_ALIGNMENT:
      return 'Pollution alignment (CAPEX based)';
    case EuTaxonomyAssessmentType.BIODIVERSITY_CAPEX_ELIGIBILITY:
      return 'Biodiversity eligibility (CAPEX based)';
    case EuTaxonomyAssessmentType.BIODIVERSITY_CAPEX_ALIGNMENT:
      return 'Biodiversity alignment (CAPEX based)';
    case EuTaxonomyAssessmentType.MITIGATION_OR_ADAPTATION_CAPEX_ELIGIBILITY:
      return 'Mitig, & Adapt. eligibility (CAPEX based)';
    case EuTaxonomyAssessmentType.MITIGATION_OR_ADAPTATION_CAPEX_ALIGNMENT:
      return 'Mitig, & Adapt. alignment (CAPEX based)';
    case EuTaxonomyAssessmentType.UNION_CAPEX_ELIGIBILITY:
      return 'Total eligibility (CAPEX based)';
    case EuTaxonomyAssessmentType.UNION_CAPEX_ALIGNMENT:
      return 'Total alignment (CAPEX based)';
    case EuTaxonomyAssessmentType.UNION_CAPEX_ALIGNMENT_ENABLING:
      return 'Total alignment (CAPEX based, enabling)';
    case EuTaxonomyAssessmentType.UNION_CAPEX_ALIGNMENT_TRANSITIONAL:
      return 'Total alignment (CAPEX based, transitional)';

    case EuTaxonomyAssessmentType.MITIGATION_OPEX_ELIGIBILITY:
      return 'Mitigation eligibility (OPEX based)';
    case EuTaxonomyAssessmentType.MITIGATION_OPEX_ALIGNMENT:
      return 'Mitigation alignment (OPEX based)';
    case EuTaxonomyAssessmentType.ADAPTATION_OPEX_ELIGIBILITY:
      return 'Adaptation eligibility (OPEX based)';
    case EuTaxonomyAssessmentType.ADAPTATION_OPEX_ALIGNMENT:
      return 'Adaptation alignment (OPEX based)';
    case EuTaxonomyAssessmentType.WATER_OPEX_ELIGIBILITY:
      return 'Water eligibility (OPEX based)';
    case EuTaxonomyAssessmentType.WATER_OPEX_ALIGNMENT:
      return 'Water alignment (OPEX based)';
    case EuTaxonomyAssessmentType.CIRCULAR_OPEX_ELIGIBILITY:
      return 'Circular eligibility (OPEX based)';
    case EuTaxonomyAssessmentType.CIRCULAR_OPEX_ALIGNMENT:
      return 'Circular alignment (OPEX based)';
    case EuTaxonomyAssessmentType.POLLUTION_OPEX_ELIGIBILITY:
      return 'Pollution eligibility (OPEX based)';
    case EuTaxonomyAssessmentType.POLLUTION_OPEX_ALIGNMENT:
      return 'Pollution alignment (OPEX based)';
    case EuTaxonomyAssessmentType.BIODIVERSITY_OPEX_ELIGIBILITY:
      return 'Biodiversity eligibility (OPEX based)';
    case EuTaxonomyAssessmentType.BIODIVERSITY_OPEX_ALIGNMENT:
      return 'Biodiversity alignment (OPEX based)';
    case EuTaxonomyAssessmentType.MITIGATION_OR_ADAPTATION_OPEX_ELIGIBILITY:
      return 'Mitig, & Adapt. eligibility (OPEX based)';
    case EuTaxonomyAssessmentType.MITIGATION_OR_ADAPTATION_OPEX_ALIGNMENT:
      return 'Mitig, & Adapt. alignment (OPEX based)';
    case EuTaxonomyAssessmentType.UNION_OPEX_ELIGIBILITY:
      return 'Total eligibility (all objectives, OPEX based)';
    case EuTaxonomyAssessmentType.UNION_OPEX_ALIGNMENT:
      return 'Total alignment (all objectives, OPEX based)';
    case EuTaxonomyAssessmentType.UNION_OPEX_ALIGNMENT_ENABLING:
      return 'Total alignment (all objectives, OPEX based, enabling)';
    case EuTaxonomyAssessmentType.UNION_OPEX_ALIGNMENT_TRANSITIONAL:
      return 'Total alignment (all objectives, OPEX based, transitional)';

    case EuTaxonomyAssessmentType.NUCLEAR_ALIGNMENT:
      return 'Nuclear alignment';
    case EuTaxonomyAssessmentType.FOSSIL_FUEL_ALIGNMENT:
      return 'Fossil fuel alignment';

    case TnfaAssessmentType.TNFA_NUCLEAR_INNOVATIVE_ELECTRICITY:
      return 'Nuclear innovative electricity generation';
    case TnfaAssessmentType.TNFA_NUCLEAR_NEW_INSTALLATIONS:
      return 'Nuclear new installations';
    case TnfaAssessmentType.TNFA_NUCLEAR_EXISTING_INSTALLATIONS:
      return 'Nuclear existing installations';
    case TnfaAssessmentType.TNFA_FOSSIL_ELECTRICITY:
      return 'Fossil gas electricity generation';
    case TnfaAssessmentType.TNFA_FOSSIL_COMBINED_HEAT_POWER:
      return 'Fossil gas combined heat/cool and power';
    case TnfaAssessmentType.TNFA_FOSSIL_HEAT_GENERATION:
      return 'Fossil gas heat generation';
  }
}
